import { useEffect, useState, useCallback } from "react";
import {
  getCourses,
  createCourse,
  updateCourse,
  deleteCourse,
} from "@services/courseService";
import { getPublicCourses } from "@services/publicCourseService";

const useCourseData = ({
  search_text = null,
  include_topics = null,
  is_public = true,
  include_my = null,
  department_id = null,
  level = null,
} = {}) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [courseCount, setCourseCount] = useState(0);
  const includeTopics = include_topics; // normalize naming
  const includeMy = include_my; // normalize naming for include_my

  // Pagination state
  const [page, setPage] = useState(1);
  const [nextUrl, setNextUrl] = useState(null);
  const [prevUrl, setPrevUrl] = useState(null);

  // Fetch courses
  const fetchCourses = useCallback(
    async ({
      search_text = null,
      department_id = null,
      faculty_id = null,
      level = null,
      with_quiz = null,
      include_topics = null,
      include_my = null,
      url = null,
      append = false,
    } = {}) => {
      setLoading(true);
      setError(null);
      try {
        const fetchFn = is_public ? getPublicCourses : getCourses;
        const response = await fetchFn({
          search_text,
          department_id,
          faculty_id,
          level,
          include_topics: ( includeTopics || include_topics), // prioritize hook-level setting
          with_quiz,
          url,
          include_my: (include_my || includeMy),
        });

        const { results = [], next = null, previous = null, count = 0 } =
          response;

        setNextUrl(next);
        setPrevUrl(previous);
        setCourseCount(count);
        setCourses((prev) => (append ? [...prev, ...results] : results));

        // Reset page if fetching from scratch
        if (!url) setPage(1);
      } catch (err) {
        console.error("Error fetching courses:", err);
        setError(err.message || "Failed to fetch courses data.");
      } finally {
        setLoading(false);
      }
    },
    [is_public]
  );

  // Pagination handlers
  const fetchNextPage = useCallback(
    (appendArg = false) => {
      if (!nextUrl) return;
      const shouldAppend = appendArg === true;

      setPage((prev) => prev + 1);
      fetchCourses({ url: nextUrl, append: shouldAppend});
    },
    [nextUrl, fetchCourses]
  );

  const fetchPreviousPage = useCallback(
    (appendArg = false) => {
      if (!prevUrl) return;
      const shouldAppend = appendArg === true;

      setPage((prev) => Math.max(1, prev - 1));
      fetchCourses({ url: prevUrl, append: shouldAppend});
    },
    [prevUrl, fetchCourses]
  );

  // CRUD operations
  const addCourse = useCallback(async (courseData) => {
    setLoading(true);
    try {
      const newCourse = await createCourse(courseData);
      setCourses((prev) => [newCourse, ...prev]);
      setCourseCount((prev) => prev + 1);
      return newCourse;
    } catch (err) {
      console.error("Error adding course:", err);
      setError(err.message || "Failed to add course.");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const editCourse = useCallback(async (courseId, courseData) => {
    setLoading(true);
    try {
      const updatedCourse = await updateCourse(courseId, courseData);
      setCourses((prev) =>
        prev.map((c) => (c.id === courseId ? updatedCourse : c))
      );
      return updatedCourse;
    } catch (err) {
      console.error("Error updating course:", err);
      setError(err.message || "Failed to update course.");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const removeCourse = useCallback(async (courseId) => {
    setLoading(true);
    try {
      await deleteCourse(courseId);
      setCourses((prev) => prev.filter((c) => c.id !== courseId));
      setCourseCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Error deleting course:", err);
      setError(err.message || "Failed to delete course.");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-fetch courses when hook parameters change
  useEffect(() => {
    fetchCourses({ search_text, include_topics, include_my, department_id, level });
  }, [search_text, include_topics, include_my, department_id, level, fetchCourses]);

  return {
    courses,
    loading,
    error,
    courseCount,
    page,
    hasNext: !!nextUrl,
    hasPrevious: !!prevUrl,
    fetchCourses,
    fetchNextPage,
    fetchPreviousPage,
    addCourse,
    editCourse,
    removeCourse,
    setCourses,
  };
};

export default useCourseData;
